// Alliance overview (spec §3.6): who we are, today's message, my rank, leaving.
import type { MemberAlliance } from '@stars-and-ships/api-types';
import { useMutation } from '@tanstack/react-query';
import { useOutletContext } from 'react-router';
import { endpoints } from '../../api/endpoints';
import type { GameContext } from '../GameLayout';
import { ErrorNotice, Notice, Panel, Stat } from '../ui';
import { ConfirmButton, RANK_LABELS, useRefresh } from './shared';

export function OverviewTab({
  galaxyId,
  alliance,
}: {
  galaxyId: string;
  alliance: MemberAlliance;
}) {
  const { me } = useOutletContext<GameContext>();
  const refresh = useRefresh(galaxyId);
  const leave = useMutation({
    mutationFn: () => endpoints.removeMember(galaxyId, alliance.id, me?.playerId ?? ''),
    onSuccess: refresh,
  });
  return (
    <div className="space-y-4">
      <Panel title="Profile">
        <div className="mb-3 grid grid-cols-2 gap-3">
          <Stat
            label="Your rank"
            value={<span data-my-rank>{RANK_LABELS[alliance.myRank]}</span>}
          />
          <Stat label="Members" value={`${alliance.memberCount}/${alliance.maxMembers}`} />
          <Stat label="Systems" value={alliance.systemCount} />
          <Stat label="Points" value={<span data-points>{alliance.points}</span>} />
        </div>
        <p data-alliance-description className="whitespace-pre-wrap text-sm text-text">
          {alliance.description || <span className="text-faint">No description yet.</span>}
        </p>
        <p className="mt-2 text-xs text-muted">
          {alliance.openRecruitment ? 'Open for applications' : 'Invitation only'}
        </p>
      </Panel>
      {alliance.myRank === 'recruit' ? (
        <Notice>
          As a Recruit you see your allies’ systems. Shared intel — scouting reports, incoming
          attacks, fleet movements and battle reports — opens at Commander rank.
        </Notice>
      ) : null}
      <Panel title="Message of the day">
        <p data-motd className="whitespace-pre-wrap text-sm text-text">
          {alliance.motd || <span className="text-faint">No message yet.</span>}
        </p>
      </Panel>
      <Panel>
        {leave.error ? <ErrorNotice error={leave.error} /> : null}
        {alliance.myRank === 'overlord' ? (
          <p className="text-sm text-muted">
            To leave, hand the leadership to another member (member list) or disband the alliance
            (Settings).
          </p>
        ) : (
          <ConfirmButton
            label="Leave alliance"
            question="Leave the alliance? You cannot join another one for 24 hours, and your reinforcements in allied systems fly home."
            confirmLabel="Leave"
            disabled={leave.isPending}
            onConfirm={() => leave.mutate()}
          />
        )}
      </Panel>
    </div>
  );
}
